import "@client/input.css";
import {App} from "@client/containers/App";
import {bootstrap} from "@client/features/boot";
import {preloadSceneDependencies} from "@client/gotterdammerung";
import {Preferences} from "@client/hardware";

import "react-day-picker/dist/style.css";
import * as analytics from "@client/shared/analytics";
import {Static} from "@client/shared/components/Static";
import * as sentry from "@client/shared/sentry";
import {makeStore, store} from "@client/store";
import {reactivate} from "@reactivated";
import React from "react";
import {createRoot, hydrateRoot} from "react-dom/client";
import ReactModal from "react-modal";
import {Provider as ReduxProvider} from "react-redux";

import {LiveSiteContext} from "./app/context";

reactivate({
    async init({context}) {
        // cordova-plugin-purchase registers browser globals at import time;
        // this entry module is evaluated during SSR too, so it loads here —
        // init never runs on the server.
        // @ts-expect-error cordova-plugin-purchase is a global script, not a module.
        await import("cordova-plugin-purchase");

        const MIXPANEL_TOKEN =
            window.cordova == null
                ? context.environment.MIXPANEL_TOKEN
                : (localStorage.getItem("MIXPANEL_TOKEN") ??
                  (await Preferences.get({key: "MIXPANEL_TOKEN"})).value ??
                  "14aa032ff169953df171a16d26b60a14");

        const SENTRY_DSN =
            window.cordova == null
                ? context.environment.SENTRY_DSN
                : (localStorage.getItem("SENTRY_DSN") ??
                  (await Preferences.get({key: "SENTRY_DSN"})).value ??
                  "https://f066a13bbfe348a7969695536ed7d026@o17373.ingest.sentry.io/1843735");

        void sentry.init(SENTRY_DSN, context.profile?.user);
        void analytics.init(MIXPANEL_TOKEN, context.profile?.user ?? context.lead);
    },
    mount({context, props, content}) {
        if (context.template_name === "App") {
            // The App shell owns rendering: a fresh client-side root over
            // the SSR markup, not hydration of it.
            if (window.location.search.includes("_ssr_only=") == false) {
                const appProps = props as {ssr_resolved?: Record<string, unknown>};
                if (appProps.ssr_resolved != null) {
                    preloadSceneDependencies(
                        window.location.pathname,
                        appProps.ssr_resolved,
                    );
                }
                analytics.processUrlParams();
                void bootstrap(store.dispatch, () => store.getState());
                const root = createRoot(document.getElementById("root")!);

                ReactModal.setAppElement("#root");

                root.render(
                    <ReduxProvider store={store}>
                        <LiveSiteContext>
                            <App />
                            <Static />
                        </LiveSiteContext>
                    </ReduxProvider>,
                );
            }
            return;
        }
        window.history.replaceState(
            {reactivated: true, url: document.location.href},
            "",
            document.location.href,
        );
        hydrateRoot(document, content);
    },
    render: (content, {ssr}) => (
        <ReduxProvider store={ssr ? makeStore() : store}>{content}</ReduxProvider>
    ),
});
